import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiArrowRight, FiArrowLeft, FiPlus, FiTrash2, FiBookOpen, FiLayers } = FiIcons;

const StructureOutline = ({ data = {}, onUpdate, onComplete }) => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    structureType: '',
    // Fiction fields
    act1: '',
    act2: '',
    act3: '',
    act4: '',
    sceneIdeas: [],
    // Nonfiction fields
    readerBefore: '',
    readerAfter: '',
    chapters: [],
    ...data
  });

  useEffect(() => {
    if (onUpdate) {
      onUpdate(formData);
    }
  }, [formData, onUpdate]);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const addSceneIdea = () => {
    setFormData(prev => ({
      ...prev,
      sceneIdeas: [...prev.sceneIdeas, { id: Date.now(), idea: '' }]
    }));
  };

  const updateSceneIdea = (id, value) => {
    setFormData(prev => ({
      ...prev,
      sceneIdeas: prev.sceneIdeas.map(scene => 
        scene.id === id ? { ...scene, idea: value } : scene
      )
    }));
  };

  const removeSceneIdea = (id) => {
    setFormData(prev => ({
      ...prev,
      sceneIdeas: prev.sceneIdeas.filter(scene => scene.id !== id)
    }));
  };

  const addChapter = () => {
    setFormData(prev => ({
      ...prev,
      chapters: [...prev.chapters, {
        id: Date.now(),
        title: '',
        bigIdea: '',
        scripture: '',
        takeaway: ''
      }]
    }));
  };

  const updateChapter = (id, field, value) => {
    setFormData(prev => ({
      ...prev,
      chapters: prev.chapters.map(chapter => 
        chapter.id === id ? { ...chapter, [field]: value } : chapter
      )
    }));
  };

  const removeChapter = (id) => {
    setFormData(prev => ({
      ...prev,
      chapters: prev.chapters.filter(chapter => chapter.id !== id)
    }));
  };

  const handleContinue = () => {
    if (onComplete) {
      onComplete();
    }
    navigate('/scope');
  };

  const handleBack = () => {
    navigate('/book-vision');
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="max-w-4xl mx-auto"
    >
      <div className="text-center mb-8">
        <h2 className="font-serif text-3xl font-bold text-stone-800 mb-3">
          Structure & Outline
        </h2>
        <p className="text-stone-600 text-lg">
          Let's build the framework that will guide your writing journey.
        </p>
      </div>

      {/* Structure Type Selection */}
      <motion.div 
        className="card mb-8"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
      >
        <div className="flex items-center mb-6">
          <SafeIcon icon={FiLayers} className="text-brand-orange mr-2" />
          <h3 className="font-semibold text-stone-800 text-xl">Choose Your Structure Type</h3>
        </div>
        
        <div className="grid md:grid-cols-2 gap-4">
          <button
            onClick={() => handleInputChange('structureType', 'fiction')}
            className={`p-6 rounded-xl border-2 transition-all duration-200 text-left ${
              formData.structureType === 'fiction'
                ? 'border-brand-orange bg-brand-orange/10'
                : 'border-stone-200 hover:border-brand-orange/50'
            }`}
          >
            <div className="flex items-center mb-2">
              <SafeIcon icon={FiBookOpen} className="text-brand-orange mr-2" />
              <h4 className="font-semibold text-stone-800">Fiction</h4>
            </div>
            <p className="text-stone-600 text-sm">
              Story-driven narrative with characters, plot, and scenes
            </p>
          </button>
          
          <button
            onClick={() => handleInputChange('structureType', 'nonfiction')}
            className={`p-6 rounded-xl border-2 transition-all duration-200 text-left ${
              formData.structureType === 'nonfiction'
                ? 'border-brand-blue bg-brand-blue/10'
                : 'border-stone-200 hover:border-brand-blue/50'
            }`}
          >
            <div className="flex items-center mb-2">
              <SafeIcon icon={FiLayers} className="text-brand-blue mr-2" />
              <h4 className="font-semibold text-stone-800">Nonfiction</h4>
            </div>
            <p className="text-stone-600 text-sm">
              Teaching-focused content with chapters and key takeaways
            </p>
          </button>
        </div>
      </motion.div>

      <AnimatePresence mode="wait">
        {formData.structureType === 'fiction' && (
          <motion.div
            key="fiction"
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: 20 }}
            className="space-y-6"
          >
            {/* Fiction Structure */}
            <div className="grid md:grid-cols-2 gap-6">
              <div className="card">
                <h4 className="font-semibold text-stone-800 mb-4">Act 1: Opening Hook</h4>
                <textarea
                  className="form-textarea"
                  placeholder="How will you draw readers in? What's the inciting incident?"
                  value={formData.act1}
                  onChange={(e) => handleInputChange('act1', e.target.value)}
                />
              </div>
              
              <div className="card">
                <h4 className="font-semibold text-stone-800 mb-4">Act 2: Midpoint Shift</h4>
                <textarea
                  className="form-textarea"
                  placeholder="What major change or revelation happens in the middle?"
                  value={formData.act2}
                  onChange={(e) => handleInputChange('act2', e.target.value)}
                />
              </div>
              
              <div className="card">
                <h4 className="font-semibold text-stone-800 mb-4">Act 3: Major Turning Point</h4>
                <textarea
                  className="form-textarea"
                  placeholder="What's the climax or major turning point?"
                  value={formData.act3}
                  onChange={(e) => handleInputChange('act3', e.target.value)}
                />
              </div>
              
              <div className="card">
                <h4 className="font-semibold text-stone-800 mb-4">Act 4: Final Resolution</h4>
                <textarea
                  className="form-textarea"
                  placeholder="How does everything resolve? What's the transformation?"
                  value={formData.act4}
                  onChange={(e) => handleInputChange('act4', e.target.value)}
                />
              </div>
            </div>

            {/* Scene Ideas */}
            <div className="card">
              <div className="flex items-center justify-between mb-4">
                <h4 className="font-semibold text-stone-800">Scene Ideas (Optional)</h4>
                <button
                  onClick={addSceneIdea}
                  className="btn-secondary flex items-center text-sm"
                  disabled={formData.sceneIdeas.length >= 10}
                >
                  <SafeIcon icon={FiPlus} className="mr-1" />
                  Add Scene
                </button>
              </div>
              
              <div className="space-y-3">
                {formData.sceneIdeas.map((scene, index) => (
                  <motion.div
                    key={scene.id}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="flex gap-3"
                  >
                    <input
                      type="text"
                      className="form-input flex-1"
                      placeholder={`Scene ${index + 1} idea...`}
                      value={scene.idea}
                      onChange={(e) => updateSceneIdea(scene.id, e.target.value)}
                    />
                    <button
                      onClick={() => removeSceneIdea(scene.id)}
                      className="text-red-500 hover:text-red-700 p-2"
                    >
                      <SafeIcon icon={FiTrash2} />
                    </button>
                  </motion.div>
                ))}
                
                {formData.sceneIdeas.length === 0 && (
                  <p className="text-stone-500 text-sm italic text-center py-4">
                    Add scene ideas to flesh out your story structure
                  </p>
                )}
              </div>
            </div>
          </motion.div>
        )}

        {formData.structureType === 'nonfiction' && (
          <motion.div
            key="nonfiction"
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: 20 }}
            className="space-y-6"
          >
            {/* Reader Transformation */}
            <div className="grid md:grid-cols-2 gap-6">
              <div className="card">
                <h4 className="font-semibold text-stone-800 mb-4">Reader's "Before"</h4>
                <textarea
                  className="form-textarea"
                  placeholder="Where is your reader starting? What struggles or questions do they have?"
                  value={formData.readerBefore}
                  onChange={(e) => handleInputChange('readerBefore', e.target.value)}
                />
              </div>
              
              <div className="card">
                <h4 className="font-semibold text-stone-800 mb-4">Reader's "After"</h4>
                <textarea
                  className="form-textarea"
                  placeholder="Where will they be after reading? What transformation will occur?"
                  value={formData.readerAfter}
                  onChange={(e) => handleInputChange('readerAfter', e.target.value)}
                />
              </div>
            </div>

            {/* Chapters */}
            <div className="card">
              <div className="flex items-center justify-between mb-6">
                <h4 className="font-semibold text-stone-800">Chapter Outline</h4>
                <button
                  onClick={addChapter}
                  className="btn-secondary flex items-center text-sm"
                  disabled={formData.chapters.length >= 10}
                >
                  <SafeIcon icon={FiPlus} className="mr-1" />
                  Add Chapter
                </button>
              </div>
              
              <div className="space-y-6">
                {formData.chapters.map((chapter, index) => (
                  <motion.div
                    key={chapter.id}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="bg-stone-50 rounded-xl p-4 border border-stone-200"
                  >
                    <div className="flex items-center justify-between mb-4">
                      <h5 className="font-medium text-stone-800">Chapter {index + 1}</h5>
                      <button
                        onClick={() => removeChapter(chapter.id)}
                        className="text-red-500 hover:text-red-700 p-1"
                      >
                        <SafeIcon icon={FiTrash2} className="text-sm" />
                      </button>
                    </div>
                    
                    <div className="grid md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-stone-700 mb-2">
                          Chapter Title
                        </label>
                        <input
                          type="text"
                          className="form-input"
                          placeholder="Chapter title..."
                          value={chapter.title}
                          onChange={(e) => updateChapter(chapter.id, 'title', e.target.value)}
                        />
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium text-stone-700 mb-2">
                          Scripture (Optional)
                        </label>
                        <input
                          type="text"
                          className="form-input"
                          placeholder="Key verse..."
                          value={chapter.scripture}
                          onChange={(e) => updateChapter(chapter.id, 'scripture', e.target.value)}
                        />
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium text-stone-700 mb-2">
                          Big Idea
                        </label>
                        <textarea
                          className="form-textarea h-20"
                          placeholder="Main concept or teaching..."
                          value={chapter.bigIdea}
                          onChange={(e) => updateChapter(chapter.id, 'bigIdea', e.target.value)}
                        />
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium text-stone-700 mb-2">
                          Key Takeaway
                        </label>
                        <textarea
                          className="form-textarea h-20"
                          placeholder="Practical application..."
                          value={chapter.takeaway}
                          onChange={(e) => updateChapter(chapter.id, 'takeaway', e.target.value)}
                        />
                      </div>
                    </div>
                  </motion.div>
                ))}
                
                {formData.chapters.length === 0 && (
                  <div className="text-center py-8">
                    <p className="text-stone-500 italic">
                      Start adding chapters to build your book outline
                    </p>
                  </div>
                )}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Navigation */}
      {formData.structureType && (
        <motion.div 
          className="flex justify-between mt-8"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.3 }}
        >
          <button
            onClick={handleBack}
            className="btn-secondary flex items-center"
          >
            <SafeIcon icon={FiArrowLeft} className="mr-2" />
            Back to Vision
          </button>
          
          <button
            onClick={handleContinue}
            className="btn-primary flex items-center"
          >
            Continue to Project Scope
            <SafeIcon icon={FiArrowRight} className="ml-2" />
          </button>
        </motion.div>
      )}
    </motion.div>
  );
};

export default StructureOutline;